/**
 * The open pipeline — §10's "Opening a File from Another Application", as code.
 *
 * A file arrives from a messaging app with a name we cannot trust, a MIME type
 * that is usually `application/octet-stream`, and no guarantee it is intact.
 * What happens between that moment and a useful first screen is the product,
 * and it is this function.
 *
 * The stages are ordered so that nothing expensive happens before something
 * cheap has ruled it out:
 *
 *   1. head bytes only            — 512 bytes, never the file
 *   2. detect                     — extension, MIME, magic bytes reconciled
 *   3. container structure        — ZIP central directory only, no extraction
 *   4. security assessment        — refusals and budgets decided before parsing
 *   5. probe                      — first page/sheet/rows for the first screen
 *   6. open                       — full parse, read-only
 *
 * §11 forbids making the user wait for step 6 to see anything, so the caller
 * gets step 5 as soon as it exists and step 6 arrives separately.
 */
import {
  FORMATS_BY_ID, applyContainerInspection, assessFile, createSession, detect,
  extensionOf, inspectEbmlHeader, inspectOle2Streams, inspectZipEntries, readZipDirectory,
  readZipMimetype,
} from '@moduviewer/core';
import type {
  DetectionResult, DocumentSession, FileStore, FormatSpec, OriginalRef,
  PlatformTier, SecurityAssessment, ZipDirectory,
} from '@moduviewer/core';
import type {
  FileSource, HandlerRegistry, OpenedDocument, Preview,
} from '@moduviewer/handlers';
import { HandlerError, openCompoundFile } from '@moduviewer/handlers';

/** Bytes read before anything decides what the file is. */
const HEAD_BYTES = 512;

/**
 * Session ids are opaque and appear in cache paths, so they must not encode
 * anything about the document — §29 keeps filenames out of temporary storage.
 */
function defaultSessionId(): string {
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

export interface OpenRequest {
  /** Platform handle: a path, a `content://` URI, a security-scoped URL. */
  readonly handle: string;
  /** Name as received. Untrusted (§30). */
  readonly displayName: string;
  /** MIME claimed by the sending app. Usually meaningless. */
  readonly declaredMime?: string | undefined;
  readonly source: OriginalRef['source'];
}

/** §11 progressive status, reported as i18n keys (§25). */
export type OpenStatus =
  | 'status.checkingFile'
  | 'status.preparingPreview'
  | 'status.firstPageReady'
  | 'status.loadingRemaining'
  | 'status.readyToEdit'
  | 'status.limitedSupport'
  | 'status.mayBeDamaged';

export interface OpenDeps {
  readonly store: FileStore;
  readonly registry: HandlerRegistry;
  readonly tier: PlatformTier;
  readonly signal: AbortSignal;
  /** Called as each stage completes so the UI can paint (§11). */
  readonly onStatus?: (status: OpenStatus) => void;
  /** Injected so tests are deterministic; defaults to a random id. */
  readonly newSessionId?: () => string;
}

/** Why a file could not be opened, with the §34 message key to show. */
export class OpenRefusedError extends Error {
  readonly messageKey: string;
  readonly detection: DetectionResult | null;
  readonly assessment: SecurityAssessment | null;

  constructor(
    messageKey: string, detail: string,
    detection: DetectionResult | null = null,
    assessment: SecurityAssessment | null = null,
  ) {
    super(detail);
    this.name = 'OpenRefusedError';
    this.messageKey = messageKey;
    this.detection = detection;
    this.assessment = assessment;
  }
}

/** Everything known after identification, before any parsing. */
export interface IdentifiedFile {
  readonly request: OpenRequest;
  readonly detection: DetectionResult;
  readonly assessment: SecurityAssessment;
  readonly spec: FormatSpec | null;
  readonly source: FileSource;
  readonly original: OriginalRef;
  readonly archive: ZipDirectory | undefined;
  /** True when structure inspection found macro storage. */
  readonly macrosPresent: boolean;
}

export interface OpenedFile extends IdentifiedFile {
  readonly preview: Preview;
  readonly document: OpenedDocument<unknown>;
  readonly session: DocumentSession;
  /** True when a handler exists and the file may actually be edited. */
  readonly editable: boolean;
}

/**
 * Identify a file: read its head, reconcile the evidence, inspect the container
 * and decide what is allowed. Nothing is parsed and nothing is decompressed.
 */
export async function identifyFile(
  request: OpenRequest, deps: OpenDeps,
): Promise<IdentifiedFile> {
  deps.onStatus?.('status.checkingFile');

  const info = await deps.store.stat(request.handle);
  if (!info.exists) {
    throw new OpenRefusedError('error.unableToRead', `no such file: ${request.handle}`);
  }

  const head = await deps.store.readRange(request.handle, 0, Math.min(HEAD_BYTES, info.sizeBytes));
  let detection = detect({
    filename: request.displayName,
    declaredMime: request.declaredMime,
    head,
    size: info.sizeBytes,
  });

  let archive: ZipDirectory | undefined;
  let macrosPresent = false;
  let wholeFile: Uint8Array | undefined;

  if (detection.needsContainerInspection) {
    // Container identification needs the directory, which lives at the end of
    // the file — the one place a head read cannot reach.
    wholeFile = await deps.store.readAll(request.handle);

    if (detection.containerKind === 'zip') {
      try {
        archive = readZipDirectory(wholeFile);
      } catch (err) {
        throw new OpenRefusedError(
          'error.damagedOrIncomplete', `container unreadable: ${(err as Error).message}`, detection,
        );
      }
      const inspection = inspectZipEntries(
        archive.entries.map((e) => e.name),
        readZipMimetype(wholeFile, archive),
        extensionOf(request.displayName),
      );
      macrosPresent = inspection.macrosPresent;
      detection = applyContainerInspection(detection, inspection);
    } else if (detection.containerKind === 'ole2') {
      // Read the compound-file directory to name the streams, so DOC/XLS/PPT
      // and HWP are told apart by their contents rather than guessed from the
      // extension (§19). A container we cannot parse is reported as unresolved.
      let streamNames: string[] = [];
      try {
        streamNames = [...openCompoundFile(wholeFile).streams.keys()];
      } catch { /* unreadable CFB — inspectOle2Streams reports it honestly */ }
      const inspection = inspectOle2Streams(streamNames);
      detection = applyContainerInspection(detection, inspection);
    } else if (detection.containerKind === 'ebml') {
      // WebM vs Matroska is decided by the DocType, which lives in the header
      // and so is already in the bytes we read — no whole-file read needed.
      const inspection = inspectEbmlHeader(head);
      if (inspection.formatId) detection = applyContainerInspection(detection, inspection);
    }
  }

  const spec = detection.formatId ? FORMATS_BY_ID.get(detection.formatId) ?? null : null;
  const assessment = assessFile({
    detection, spec, sizeBytes: info.sizeBytes, tier: deps.tier, archive, macrosPresent,
  });

  if (!assessment.canOpen) {
    const refusal = assessment.refusals[0]!;
    throw new OpenRefusedError(refusal.messageKey, refusal.detail, detection, assessment);
  }

  const bytes = wholeFile ?? await deps.store.readAll(request.handle);

  return {
    request,
    detection,
    assessment,
    spec,
    source: {
      bytes,
      filename: request.displayName,
      formatId: detection.formatId ?? 'txt',
      sizeBytes: info.sizeBytes,
    },
    original: {
      handle: request.handle,
      displayName: request.displayName,
      formatId: detection.formatId,
      sizeBytes: info.sizeBytes,
      modifiedAtMs: info.modifiedAtMs,
      source: request.source,
    },
    archive,
    macrosPresent,
  };
}

/**
 * Open a file end to end.
 *
 * @param identified Pass the result of {@link identifyFile} to avoid repeating
 *   the identification work when the caller has already done it.
 */
export async function openFile(
  request: OpenRequest, deps: OpenDeps, identified?: IdentifiedFile,
): Promise<OpenedFile> {
  const id = identified ?? await identifyFile(request, deps);
  const { detection, assessment, source } = id;

  const handler = detection.formatId ? deps.registry.get(detection.formatId) : null;
  if (!handler) {
    deps.onStatus?.('status.limitedSupport');
    throw new OpenRefusedError(
      'detect.warning.unsupportedFormat',
      `no handler registered for ${detection.formatId ?? 'unknown format'}`,
      detection, assessment,
    );
  }

  const ctx = { assessment, limits: assessment.limits, signal: deps.signal };

  deps.onStatus?.('status.preparingPreview');
  let preview: Preview;
  try {
    preview = await handler.probe(source, ctx);
  } catch (err) {
    if (err instanceof HandlerError) {
      deps.onStatus?.('status.mayBeDamaged');
      throw new OpenRefusedError(err.messageKey, err.message, detection, assessment);
    }
    throw err;
  }
  deps.onStatus?.('status.firstPageReady');

  deps.onStatus?.('status.loadingRemaining');
  let document: OpenedDocument<unknown>;
  try {
    document = await handler.open(source, ctx);
  } catch (err) {
    if (err instanceof HandlerError) {
      deps.onStatus?.('status.mayBeDamaged');
      throw new OpenRefusedError(err.messageKey, err.message, detection, assessment);
    }
    throw err;
  }

  // §4.7 — the session starts read-only no matter what the handler reports.
  const session = createSession({
    id: deps.newSessionId?.() ?? defaultSessionId(),
    original: id.original,
    forceReadOnly: assessment.forceReadOnly || document.readOnly,
    lossWarnings: document.lossOnSave,
  });

  const editable = !session.forceReadOnly
    && detection.formatId !== null
    && deps.registry.canEditFormat(detection.formatId);

  deps.onStatus?.(editable ? 'status.readyToEdit' : 'status.limitedSupport');

  return { ...id, preview, document, session, editable };
}
